/**
 * Website service covering pageview tracking and management endpoints, refactored from api.ts without behavior changes.
 */

import api from "@/utils/axios";
import { API_BASE_URL } from "./apiBase";
import { getAccessToken } from "@/utils/accessTokenStore";
import type { PagedResponse } from "@/types/pagination.types";
import { createRequestDedupCache } from "./requestDedupCache";
import { formatRegionLabel, formatRegionPath } from "@/utils/regionLabel";

const managedPagedRequestCache = createRequestDedupCache({
  ttlMs: 20_000,
  maxEntries: 100,
  orphanAbortDelayMs: 150,
});

export const invalidateManagedWebsitesPagedCache = () => {
  managedPagedRequestCache.clear();
};

/**
 * Enumeration of possible website statuses. Should be equal to WebsiteStatus enum in the backend.
 *
 */
export type WebsiteStatus =
  | "SUGGESTED"
  | "ACTIVE"
  | "INACTIVE"
  | "REJECTED"
  | "ARCHIVED"
  | "DELETED";

/**
 * Interface representing a managed (edited) website.
 *
 */
export interface ManagedWebsite {
  id: string;
  title: string;
  description: string;
  url: string;
  address?: string | null;
  zipCode?: string | null;
  addedAt?: string | null;
  addedBy?: string | null;
  category1Id?: string | null;
  category1Name?: string | null;
  region?: string | null;
  regionNameEn?: string | null;
  regionId?: string | null;
  categoryPathIds?: string[] | null;
  categoryPathNames?: string[] | null;
  regionPathIds?: string[] | null;
  regionPathNames?: string[] | null;
  regionPathNamesEn?: string[] | null;
  status?: WebsiteStatus;
}

export interface SuggestedWebsiteResponse {
  id: string;
  title: string;
  description: string;
  urlLandingPage?: string | null;
  address?: string | null;
  zipCode?: string | null;
  status?: WebsiteStatus | null;
  addedAt?: string | null;
  suggestedBy?: string | null;
  category1Id?: string | null;
  category1Name?: string | null;
  region?: string | null;
  regionNameEn?: string | null;
  regionId?: string | null;
  categoryPathIds?: string[] | null;
  categoryPathNames?: string[] | null;
  regionPathIds?: string[] | null;
  regionPathNames?: string[] | null;
  regionPathNamesEn?: string[] | null;
}

/**
 * Interface representing the payload to update a website.
 *
 */
export interface UpdateWebsitePayload {
  title: string;
  description: string;
  url: string;
  address?: string | null;
  zipCode?: string | null;
  category1Id?: string | null;
  categoryPathIds?: string[] | null;
  regionId?: string | null;
  regionPathIds?: string[] | null;
}

/**
 * Builds authorization headers for authenticated API calls.
 *
 * @returns The authorization headers.
 */
const authHeaders = (): Record<string, string> => {
  const token = typeof window !== "undefined" ? getAccessToken() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Maps a raw API response payload to a ManagedWebsite object.
 *
 * @param payload The raw API response payload.
 * @returns A ManagedWebsite object.
 */
const mapManagedWebsite = (payload: any): ManagedWebsite => ({
  id: payload.id,
  title: payload.title,
  description: payload.description,
  url: payload.urlLandingPage ?? payload.url ?? "",
  address: payload.address ?? null,
  zipCode: payload.zipCode ?? null,
  addedAt: payload.addedAt ?? null,
  addedBy: payload.addedBy ?? null,
  category1Id: payload.category1Id ?? null,
  category1Name: payload.category1Name ?? null,
  region: formatRegionLabel(payload.region ?? null, payload.regionNameEn ?? null) || null,
  regionNameEn: payload.regionNameEn ?? null,
  regionId: payload.regionId ?? null,
  categoryPathIds: payload.categoryPathIds ?? null,
  categoryPathNames: payload.categoryPathNames ?? null,
  regionPathIds: payload.regionPathIds ?? null,
  regionPathNames: formatRegionPath(payload.regionPathNames ?? [], payload.regionPathNamesEn ?? []),
  regionPathNamesEn: payload.regionPathNamesEn ?? null,
  status: payload.status ?? undefined,
});

const mapSuggestedWebsite = (payload: any): SuggestedWebsiteResponse => ({
  id: payload.id,
  title: payload.title,
  description: payload.description,
  urlLandingPage: payload.urlLandingPage ?? payload.url ?? null,
  address: payload.address ?? null,
  zipCode: payload.zipCode ?? null,
  status: payload.status ?? null,
  addedAt: payload.addedAt ?? null,
  suggestedBy: payload.suggestedBy ?? null,
  category1Id: payload.category1Id ?? null,
  category1Name: payload.category1Name ?? null,
  region: formatRegionLabel(payload.region ?? null, payload.regionNameEn ?? null) || null,
  regionNameEn: payload.regionNameEn ?? null,
  regionId: payload.regionId ?? null,
  categoryPathIds: payload.categoryPathIds ?? null,
  categoryPathNames: payload.categoryPathNames ?? null,
  regionPathIds: payload.regionPathIds ?? null,
  regionPathNames: formatRegionPath(payload.regionPathNames ?? [], payload.regionPathNamesEn ?? []),
  regionPathNamesEn: payload.regionPathNamesEn ?? null,
});

/**
 * Endpoint to increment pageviews for a website.
 *
 * Axios alone cannot reproduce fetch keepalive semantics, so we prefer navigator.sendBeacon
 * to preserve the original fire-and-forget behavior of fetch(..., { keepalive: true }).
 */
export function recordPageview(websiteId: string): void {
  if (!websiteId) return;
  const url = `${API_BASE_URL}/api/websites/${encodeURIComponent(
    websiteId
  )}/pageviews`;

  // keepalive to allow closing tab.
  if (
    typeof navigator !== "undefined" &&
    typeof navigator.sendBeacon === "function"
  ) {
    try {
      navigator.sendBeacon(url);
    } catch {
      // Skip errors for usability.
      return;
    }
  }

}

/**
 * Loads managed websites.
 *
 * @param limit maximum results to request (clamped to 1..500)
 * @returns managed websites; throws when the backend response is not ok
 */
export async function getManagedWebsites(
  limit = 50,
  lang: string = "de"
): Promise<ManagedWebsite[]> {
  const res = await api.get(
    `${API_BASE_URL}/api/websites/manage?limit=${encodeURIComponent(
      String(Math.max(1, Math.min(500, Math.floor(limit))))
    )}&lang=${encodeURIComponent(lang)}`,
    {
      headers: { Accept: "application/json", ...authHeaders() },
      validateStatus: () => true,
    }
  );
  if (res.status < 200 || res.status >= 300) {
    throw new Error(`Managed websites request failed with status ${res.status}`);
  }
  return Array.isArray(res.data) ? res.data.map(mapManagedWebsite) : [];
}

export interface ManagedWebsitePagedQuery {
  page?: number;
  size?: number;
  query?: string;
  sortBy?: string;
  dateFilter?: string;
  lang?: string;
}

interface RequestOptions {
  signal?: AbortSignal;
}

export async function getManagedWebsitesPaged(
  params: ManagedWebsitePagedQuery = {},
  options: RequestOptions = {}
): Promise<PagedResponse<ManagedWebsite>> {
  const query = new URLSearchParams();
  query.set("page", String(Math.max(0, params.page ?? 0)));
  query.set("size", String(Math.max(1, Math.min(100, params.size ?? 10))));
  query.set("sortBy", (params.sortBy ?? "relevance").trim() || "relevance");
  query.set("dateFilter", (params.dateFilter ?? "all").trim() || "all");
  if (params.query && params.query.trim()) {
    query.set("query", params.query.trim());
  }
  query.set("lang", (params.lang ?? "de").trim() || "de");

  const queryString = query.toString();
  const cacheKey = `manage:${queryString}`;

  return managedPagedRequestCache.run({
    key: cacheKey,
    signal: options.signal,
    load: async (requestSignal) => {
      const res = await api.get<PagedResponse<any>>(
        `${API_BASE_URL}/api/websites/manage/paged?${queryString}`,
        {
          headers: { Accept: "application/json", ...authHeaders() },
          signal: requestSignal,
        }
      );

      if (res.status < 200 || res.status >= 300) {
        throw new Error(
          `Managed websites paged request failed with status ${res.status}`
        );
      }

      const payload = res.data as PagedResponse<any>;
      return {
        items: Array.isArray(payload?.items)
          ? payload.items.map(mapManagedWebsite)
          : [],
        page: Number(payload?.page ?? 0),
        size: Number(payload?.size ?? 10),
        totalElements: Number(payload?.totalElements ?? 0),
        totalPages: Number(payload?.totalPages ?? 0),
      };
    },
  });
}

/**
 * Loads suggested websites (status SUGGESTED) for moderation.
 *
 * @param limit maximum results to request (clamped to 1..500)
 */
export async function getSuggestedWebsites(
  limit = 100,
  lang: string = "de"
): Promise<SuggestedWebsiteResponse[]> {
  const safeLimit = Math.max(1, Math.min(500, Math.floor(limit)));
  const res = await api.get(
    `${API_BASE_URL}/api/websites/suggestions?limit=${encodeURIComponent(
      String(safeLimit)
    )}&lang=${encodeURIComponent(lang)}`,
    {
      headers: { Accept: "application/json", ...authHeaders() },
      validateStatus: () => true,
    }
  );
  if (res.status < 200 || res.status >= 300) {
    throw new Error(`Load suggestions failed with status ${res.status}`);
  }
  return Array.isArray(res.data) ? res.data.map(mapSuggestedWebsite) : [];
}

/**
 * Sends updates for a managed website to the backend.
 *
 * @param id The UUID of the website to update.
 * @param updates The payload containing the updates to apply.
 * @returns The updated ManagedWebsite object.
 */
export async function updateWebsite(
  id: string,
  updates: UpdateWebsitePayload
): Promise<ManagedWebsite> {
  const res = await api.put(
    `${API_BASE_URL}/api/websites/${id}`,
    {
      title: updates.title,
      description: updates.description,
      urlLandingPage: updates.url,
      address: updates.address ?? null,
      zipCode: updates.zipCode ?? null,
      category1Id: updates.category1Id ?? null,
      categoryPathIds: updates.categoryPathIds ?? null,
      regionId: updates.regionId ?? null,
      regionPathIds: updates.regionPathIds ?? null,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(),
      },
      validateStatus: () => true,
    }
  );
  if (res.status < 200 || res.status >= 300)
    throw new Error(`Update website failed with status ${res.status}`);
  return mapManagedWebsite(res.data);
}

/**
 * Updates a suggested website without changing its status.
 *
 * @param id The UUID of the suggestion to update.
 * @param updates The payload containing the updates to apply.
 */
export async function updateSuggestion(
  id: string,
  updates: UpdateWebsitePayload
): Promise<void> {
  const res = await api.put(
    `${API_BASE_URL}/api/websites/suggestions/${id}`,
    {
      title: updates.title,
      description: updates.description,
      urlLandingPage: updates.url,
      address: updates.address ?? null,
      zipCode: updates.zipCode ?? null,
      category1Id: updates.category1Id ?? null,
      categoryPathIds: updates.categoryPathIds ?? null,
      regionId: updates.regionId ?? null,
      regionPathIds: updates.regionPathIds ?? null,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(),
      },
      validateStatus: () => true,
    }
  );
  if (res.status < 200 || res.status >= 300) {
    throw new Error(`Update suggestion failed with status ${res.status}`);
  }
}

export interface CreateWebsitePayload {
  title: string;
  description: string;
  url: string;
  address?: string | null;
  zipCode?: string | null;
  category1Id?: string | null;
  categoryPathIds?: string[] | null;
  regionId?: string | null;
  regionPathIds?: string[] | null;
}

/**
 * Creates a new website that is immediately active.
 * @param payload The payload containing the website details.
 * @returns The created ManagedWebsite object.
 */
export async function createWebsite(
  payload: CreateWebsitePayload
): Promise<ManagedWebsite> {
  const res = await api.post(
    `${API_BASE_URL}/api/websites`,
    {
      title: payload.title,
      description: payload.description,
      urlLandingPage: payload.url,
      address: payload.address ?? null,
      zipCode: payload.zipCode ?? null,
      category1Id: payload.category1Id ?? null,
      categoryPathIds: payload.categoryPathIds ?? null,
      regionId: payload.regionId ?? null,
      regionPathIds: payload.regionPathIds ?? null,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...authHeaders(),
      },
      validateStatus: () => true,
    }
  );

  if (res.status < 200 || res.status >= 300) {
    throw new Error(`Create website failed with status ${res.status}`);
  }

  return mapManagedWebsite(res.data);
}

/**
 * Updates the status of a managed website in the backend.
 *
 * @param id The UUID of the website to update.
 * @param status The new status to set.
 */
export async function updateWebsiteStatus(
  id: string,
  status: WebsiteStatus
): Promise<void> {
  const res = await api.patch(
    `${API_BASE_URL}/api/websites/${id}/status`,
    {
      status,
    },
    {
      headers: { "Content-Type": "application/json", ...authHeaders() },
      validateStatus: () => true,
    }
  );
  if (res.status < 200 || res.status >= 300)
    throw new Error(`Update website status failed with status ${res.status}`);
}

/**
 * Deletes a website in the backend.
 * Currently this is done by setting the status to "DELETED".
 * In order to allow for easier future changes, we have a dedicated delete function.
 *
 * @param id The UUID of the website to delete.
 */
export async function deleteWebsite(id: string): Promise<void> {
  const res = await api.delete(`${API_BASE_URL}/api/websites/${id}`, {
    headers: { ...authHeaders() },
    validateStatus: () => true,
  });
  if (res.status < 200 || res.status >= 300)
    throw new Error(`Delete website failed with status ${res.status}`);
}
